import type { CustomRequestOptions } from '@/interceptors/request'
import realtimeLog from '@/utils/realtimeLog'
import { ShowMessage } from '@/utils/request/enum'

export function logHttpError(
  options: CustomRequestOptions,
  error: unknown,
  extra?: Record<string, unknown>,
  label = 'http request error',
) {
  const logData = {
    method: options.method,
    url: options.url,
    query: options.query,
    ...extra,
    error,
  }

  const logTitle = `[${label}]`

  console.error(logTitle, logData)
  realtimeLog.error(logTitle, logData)
}

// 处理错误信息
export function errorHandler(res: UniApp.RequestSuccessCallbackResult): IRejectData {
  const errData = res.data as IResData<any>

  // 默认错误code 1001
  return {
    statusCode: res.statusCode,
    code: errData.code ?? 1001,
    message: errData?.message ?? ShowMessage(errData?.code ?? res.statusCode),
    data: res.data ?? null,
  }
}

/**
 * 批量执行函数数组，每隔200毫秒执行一批（每批9个）
 * @param functions 要执行的函数数组
 */
export function executeFunctionsInBatches(functions: Array<() => void>): void {
  let index = 0
  const BATCH_SIZE = 9 // 每批执行的数量

  const executeBatch = () => {
    // 计算当前批次要执行的函数
    const batch = functions.slice(index, index + BATCH_SIZE)

    // 执行当前批次的所有函数
    batch.forEach(fn => fn())

    // 更新索引位置
    index += BATCH_SIZE

    // 如果还有剩余函数，设置下一批执行,没有剩余，清空数组
    if (index < functions.length) {
      setTimeout(executeBatch, 200) // 等待200秒执行下一批
    }
    else {
      functions.length = 0
    }
  }

  // 启动第一批执行
  executeBatch()
}

interface RetryOptions {
  maxRetryTimes?: number // 最大重试次数，默认3次
  retryDelay?: number // 每次重试延迟（毫秒），默认1000ms
  onRetry?: (retryCount: number, error: unknown) => void // 重试时的回调
}

/**
 * 带重试机制的请求函数
 * @param requestFn 返回 Promise 的函数
 * @param options 重试配置选项
 * @returns Promise<T>
 */
export async function requestWithRetry<T>(
  requestFn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<T> {
  const {
    maxRetryTimes = 2,
    retryDelay = 1000,
    onRetry,
  } = options

  // 当前重试次数
  let retryCount = 0
  const executeRequest = async (): Promise<T> => {
    try {
      const result = await requestFn()
      return result
    }
    catch (err: unknown) {
      // 判断是否需要重试
      const needRetry = retryCount < maxRetryTimes

      // 如果不需要重试，抛出错误
      if (!needRetry) {
        const errorMessage = err as { message: string }
        throw new Error(`${errorMessage.message}`)
      }

      // 记录重试次数
      retryCount++

      // 触发重试回调
      if (onRetry) {
        onRetry(retryCount, err)
      }

      // 延迟后重试
      if (retryDelay > 0) {
        await new Promise<void>(resolve => setTimeout(resolve, retryDelay))
      }

      return executeRequest()
    }
  }

  return executeRequest()
}
